import { useRef, useCallback, useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../constants/ApiEndpoints';
import { apiRequest } from '../Api';

/**
 * Hook for debounced auto-save of questionnaire answers
 * Saves partial responses to backend every 2 seconds of inactivity
 *
 * @param {number} responseId - Questionnaire response ID
 * @param {Object} answers - Current answers object {linkId: value}
 * @param {boolean} enabled - Whether auto-save is enabled (default: true)
 * @returns {Object} { save, isSaving, lastSavedAt, error, cancel }
 */
export const useAutoSave = (responseId, answers, enabled = true) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const saveTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Perform the save operation
   */
  const save = useCallback(async (answersToSave) => {
    if (!responseId || !answersToSave || Object.keys(answersToSave).length === 0) {
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsSaving(true);
    setError(null);

    try {
      const response = await apiRequest(
        API_ENDPOINTS.QUESTIONNAIRE.UPDATE_RESPONSE(responseId),
        {
          method: 'PATCH',
          body: JSON.stringify(answersToSave),
          signal: abortControllerRef.current.signal
        }
      );

      if (response.updated) {
        setLastSavedAt(new Date());
      }
    } catch (err) {
      // Don't set error if request was aborted (user navigated away)
      if (err.name !== 'AbortError') {
        setError(err.message);
        console.error('Auto-save failed:', err);
      }
    } finally {
      setIsSaving(false);
    }
  }, [responseId]);

  /**
   * Debounced save triggered on answer changes
   */
  const debouncedSave = useCallback(
    (answersToSave) => {
      // Clear existing timer
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      // Set new timer (2 second debounce)
      if (enabled) {
        saveTimerRef.current = setTimeout(() => {
          save(answersToSave);
        }, 2000);
      }
    },
    [save, enabled]
  );

  /**
   * Cancel pending save
   */
  const cancel = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Trigger debounced save when answers change
   */
  useEffect(() => {
    debouncedSave(answers);

    // Cleanup on unmount - cancel pending saves
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [answers, debouncedSave]);

  return {
    save,
    isSaving,
    lastSavedAt,
    error,
    cancel,
  };
};

export default useAutoSave;
